// Configuration for scope-gated post-auth consent (3LO).
//
// Certain OAuth scopes requested at sign-in require the user to explicitly consent to the
// application accessing their Skill Builder account before the flow completes. Today the only such
// scope is `ea/readonly` (Expert Avatar), but this map is the extension point for future scopes:
// add an entry keyed by the scope with the application's display name.

export interface ScopeConsentConfig {
  /** Human-readable application name shown in the consent copy (replaces the mockup's "ChatGPT"). */
  appName: string;
}

/**
 * Scopes that trigger the consent screen, mapped to their consent configuration. Keyed by the exact
 * OAuth scope string as it appears in the sign-in request's `scopes`/`scope` parameter.
 */
export const CONSENT_REQUIRED_SCOPES: Record<string, ScopeConsentConfig> = {
  'ea/readonly': { appName: 'Expert Avatar' },
  'quick/readonly': { appName: 'Quick' },
};

/**
 * Returns the raw list of scopes from the sign-in request URL. Cognito's authorize request uses the
 * singular `scope` param (space-delimited); some callers use the plural `scopes`. We read whichever
 * is present and split on whitespace and commas to be tolerant of both encodings.
 */
export function getRequestedScopes(search: string): string[] {
  const params = new URLSearchParams(search);
  const raw = params.get('scope') ?? params.get('scopes') ?? '';
  return raw
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * Returns the consent configuration for the first requested scope that requires consent, or
 * undefined if none of the requested scopes require consent.
 */
export function getConsentConfigForRequest(
  search: string
): (ScopeConsentConfig & { scope: string }) | undefined {
  for (const scope of getRequestedScopes(search)) {
    if (Object.prototype.hasOwnProperty.call(CONSENT_REQUIRED_SCOPES, scope)) {
      return { ...CONSENT_REQUIRED_SCOPES[scope], scope };
    }
  }
  return undefined;
}
